import { useState } from 'react';
import { Mail, MapPin, Send, HelpCircle, Handshake, MessageSquare, Clock } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { SEO } from '../components/SEO';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-16" data-testid="contact-page">
      <SEO 
        title="Contact Us"
        description="Get in touch with Turcoaz Apps. We'd love to hear your questions, feedback, or suggestions about our products."
        canonical="/contact"
      />
      {/* Header */}
      <section className="py-20 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p 
              className="text-xs tracking-[0.2em] uppercase font-semibold text-teal-700 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Contact Us
            </p>
            <h1 
              className="text-4xl sm:text-5xl tracking-tight leading-none font-semibold text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Let's <span className="text-teal-600">Connect</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Have questions, feedback, or just want to say hello? We'd love to hear from you. Fill out the form below or reach out directly.
            </p>
          </div>
        </div>
      </section>

      {/* When to Contact Us */}
      <section className="py-16 sm:py-20 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 
              className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-900 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              How We Can Help
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Whether you need help with one of our tools, want to explore a business partnership, or simply have a question about how we handle your data, we're here. Choose the category that best describes your inquiry so we can get you to the right person quickly.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-slate-50 rounded-xl p-6" data-testid="contact-support-section">
              <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                <HelpCircle className="w-6 h-6 text-teal-600" />
              </div>
              <h3 
                className="text-lg font-medium text-slate-900 mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Product Support
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Having trouble with Smart PDF Tools or any of our services? Let us know what happened, which tool you were using, and any error messages you received. We'll investigate and get back to you promptly.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6" data-testid="contact-partnerships-section">
              <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                <Handshake className="w-6 h-6 text-teal-600" />
              </div>
              <h3 
                className="text-lg font-medium text-slate-900 mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Partnerships &amp; Business
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Interested in integrating our tools into your workflow, exploring API access, or discussing a collaboration? We're open to partnerships that align with our mission of making document tools accessible to everyone.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6" data-testid="contact-general-section">
              <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                <MessageSquare className="w-6 h-6 text-teal-600" />
              </div>
              <h3 
                className="text-lg font-medium text-slate-900 mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                General Questions
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Curious about our privacy practices, have feedback on your experience, or want to suggest a new feature? We read every message and appreciate hearing from the people who use our tools.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Form */}
            <div>
              <h2 
                className="text-2xl font-medium text-slate-900 mb-8"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Send Us a Message
              </h2>
              
              {submitted ? (
                <div 
                  className="bg-teal-50 border border-teal-200 rounded-xl p-8 text-center"
                  data-testid="contact-success-message"
                >
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send className="w-8 h-8 text-teal-600" />
                  </div>
                  <h3 
                    className="text-xl font-medium text-slate-900 mb-2"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Message Sent!
                  </h3>
                  <p className="text-slate-600">
                    Thank you for reaching out. We'll get back to you as soon as possible.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 text-teal-700 hover:text-teal-800 font-medium"
                    data-testid="send-another-button"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-slate-700 mb-2 block">
                      Your Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="h-11"
                      data-testid="contact-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-2 block">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="h-11"
                      data-testid="contact-email-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="message" className="text-sm font-medium text-slate-700 mb-2 block">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="How can we help you?"
                      rows={5}
                      className="resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  {error && (
                    <div className="text-red-600 text-sm" data-testid="contact-error-message">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-3.5 rounded-md text-base font-medium hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-form-submit"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <Send className="w-5 h-5" />
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <h2 
                className="text-2xl font-medium text-slate-900 mb-8"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Contact Information
              </h2>
              
              <div className="space-y-6">
                <div 
                  className="flex items-start gap-4 p-6 bg-slate-50 rounded-xl"
                  data-testid="contact-email-info"
                >
                  <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 
                      className="text-lg font-medium text-slate-900 mb-1"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      Email
                    </h3>
                    <a 
                      href="mailto:contact@turcoazapps.com"
                      className="text-slate-600 hover:text-teal-600 transition-colors"
                    >
                      contact@turcoazapps.com
                    </a>
                  </div>
                </div>

                <div 
                  className="flex items-start gap-4 p-6 bg-slate-50 rounded-xl"
                  data-testid="contact-location-info"
                >
                  <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 
                      className="text-lg font-medium text-slate-900 mb-1"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      Location
                    </h3>
                    <p className="text-slate-600">
                      Remote-first company serving customers worldwide
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 bg-teal-50/50 rounded-xl border border-teal-100">
                <h3 
                  className="text-lg font-medium text-slate-900 mb-2"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Response Time
                </h3>
                <p className="text-slate-600">
                  We typically respond within 24-48 hours during business days. For urgent matters, please mention it in your message.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
